/**
 * MetaProbe Metadata Extractor
 * Comprehensive AI detection for images (local) and videos (API)
 * Supports 20+ AI generators with confidence scoring
 */

import exifr from 'exifr';
import { createFile as createMP4File } from 'mp4box';
import { readC2pa } from './c2paScanner';

// ============================================================================
// AI GENERATOR DETECTION PATTERNS
// ============================================================================

// IMPORTANT: Only use longer, specific keywords to avoid false positives
const AI_SOFTWARE_SIGNATURES = {
    'DALL-E': ['dall-e', 'dalle', 'openai.com'],
    'Midjourney': ['midjourney'],  // Removed 'mj' - causes false positives
    'Stable Diffusion': ['stable diffusion', 'automatic1111', 'a1111 webui', 'invoke ai'],
    'ComfyUI': ['comfyui', 'comfy ui'],
    'Adobe Firefly': ['adobe firefly', 'firefly ai'],
    'Imagen': ['google imagen'],
    'Leonardo.AI': ['leonardo.ai', 'leonardo ai'],
    'Playground AI': ['playground ai'],
    'NightCafe': ['nightcafe studio'],
    'Artbreeder': ['artbreeder'],
    'DeepAI': ['deepai.org'],
    'Craiyon': ['craiyon.com', 'dall-e mini'],
    'Jasper Art': ['jasper art'],
    'Bing Image Creator': ['bing image creator', 'microsoft designer'],
    'Flux': ['flux.1', 'black forest labs'],
    'Ideogram': ['ideogram.ai'],
    'Krea': ['krea.ai'],
    'Tensor.Art': ['tensor.art'],
    'Civitai': ['civitai.com'],
    'SeaArt': ['seaart.ai'],
    // Video AI - use specific signatures only
    'Runway': ['runway gen', 'runwayml', 'gen-2 by', 'gen-3 by'],
    'Pika': ['pika labs', 'pika.art'],
    'Sora': ['openai sora'],
    'Kling': ['kling ai', 'kling 1.0', 'kling 1.5', 'kuaishou kling'],
    'Luma': ['luma dream machine', 'luma ai', 'dream machine ai'],
    'Haiper': ['haiper ai', 'haiper.ai'],
    'Hailuo': ['hailuo ai', 'minimax video'],
    'Veo': ['google veo', 'deepmind veo'],
    'Wan': ['alibaba wan', 'tongyi wanxiang'],
    'LTX': ['ltx studio', 'lightricks ltx'],
    'Synthesia': ['synthesia.io', 'synthesia ai'],
    'HeyGen': ['heygen.com', 'heygen ai'],
    'D-ID': ['d-id.com', 'd-id ai'],
    'Stable Video': ['stable video diffusion'],
    'Genmo': ['genmo.ai'],
    'Morph Studio': ['morph studio ai'],
    'Pixverse': ['pixverse.ai'],
    'Vidu': ['vidu.io'],
    'CogVideo': ['cogvideo', 'cogvideox'],
};

// Midjourney detection patterns
const MIDJOURNEY_PATTERNS = [
    /--ar\s+\d+:\d+/i,
    /--v\s+\d+(\.\d+)?/i,
    /--style\s+\w+/i,
    /--q\s+\d+(\.\d+)?/i,
    /--s\s+\d+/i,
    /--c\s+\d+/i,
    /--seed\s+\d+/i,
    /--no\s+/i,
    /--niji/i,
    /--tile/i,
    /--weird/i,
    /--chaos/i,
    /\/imagine\s+/i,
    /Job ID:\s*[a-f0-9-]+/i,
    /Variations\s+by\s+/i,
    /upscaled\s+by\s+/i,
    /Upscaled\s+\(.*\)\s+by/i,
];

// Stable Diffusion detection patterns
const STABLE_DIFFUSION_PATTERNS = [
    /Steps:\s*\d+/i,
    /Sampler:\s*\w+/i,
    /CFG\s*scale:\s*[\d.]+/i,
    /Seed:\s*\d+/i,
    /Model:\s*[\w\s-]+/i,
    /Model\s*hash:\s*[a-f0-9]+/i,
    /Negative\s*prompt:/i,
    /Denoising\s*strength:\s*[\d.]+/i,
    /Clip\s*skip:\s*\d+/i,
    /Size:\s*\d+x\d+/i,
    /Hires\s*upscale:/i,
    /VAE:\s*[\w-]+/i,
    /<lora:/i,
    /LoRA:/i,
    /controlnet/i,
    /Hires\s*fix:/i,
];

// DALL-E detection patterns
const DALLE_PATTERNS = [
    /DALL[-·]?E/i,
    /openai\.com/i,
    /Created\s+with\s+DALL/i,
];

// Flux detection patterns
const FLUX_PATTERNS = [
    /flux/i,
    /black\s*forest\s*labs/i,
    /FLUX\.\d+/i,
];

// Adobe Firefly patterns
const FIREFLY_PATTERNS = [
    /firefly/i,
    /adobe\s*ai/i,
    /generative\s*fill/i,
    /generative\s*expand/i,
];

/**
 * Analyze text for AI generation patterns
 * Returns { generator, prompt, confidence }
 */
function detectAIFromString(text) {
    if (!text || text.length < 10) {
        return { generator: null, prompt: null, confidence: 0 };
    }

    // Limit text to scan to prevent memory issues
    const MAX_TEXT_LENGTH = 100000; // 100KB max
    const textToCheck = text.length > MAX_TEXT_LENGTH ? text.substring(0, MAX_TEXT_LENGTH) : text;
    const textLower = textToCheck.toLowerCase();
    let generator = null;
    let prompt = null;
    let confidence = 0;

    // Check for Midjourney patterns
    const mjMatches = MIDJOURNEY_PATTERNS.filter(p => p.test(textToCheck)).length;
    if (mjMatches >= 2) {
        generator = 'Midjourney';
        confidence = Math.min(0.5 + (mjMatches * 0.15), 1.0);
        // Don't store entire text as prompt - just note it was detected
        prompt = '[Midjourney patterns detected in metadata]';
    }

    // Check for Stable Diffusion patterns
    const sdMatches = STABLE_DIFFUSION_PATTERNS.filter(p => p.test(textToCheck)).length;
    if (sdMatches >= 2 && (!generator || sdMatches > mjMatches)) {
        generator = 'Stable Diffusion';
        confidence = Math.min(0.5 + (sdMatches * 0.1), 1.0);
        prompt = '[Stable Diffusion patterns detected in metadata]';
    }

    // Check for DALL-E patterns
    if (DALLE_PATTERNS.some(p => p.test(textToCheck))) {
        generator = 'DALL-E';
        confidence = 0.9;
        prompt = '[DALL-E signature detected]';
    }

    // Check for Flux patterns
    if (FLUX_PATTERNS.some(p => p.test(textToCheck))) {
        generator = 'Flux';
        confidence = 0.85;
        prompt = '[Flux signature detected]';
    }

    // Check for Firefly patterns
    if (FIREFLY_PATTERNS.some(p => p.test(textToCheck))) {
        generator = 'Adobe Firefly';
        confidence = 0.85;
        prompt = '[Adobe Firefly signature detected]';
    }

    // Check software signatures
    for (const [genName, signatures] of Object.entries(AI_SOFTWARE_SIGNATURES)) {
        for (const sig of signatures) {
            if (textLower.includes(sig)) {
                generator = genName;
                confidence = Math.max(confidence, 0.95);
                prompt = `[${genName} signature detected]`;
                break;
            }
        }
        if (generator) break;
    }

    return { generator, prompt, confidence };
}

/**
 * Extract metadata from a file
 */
export async function extractMetadata(file) {
    const fileType = file.type;

    if (fileType.startsWith('image/')) {
        return extractImageMetadata(file);
    } else if (fileType.startsWith('video/')) {
        return extractVideoMetadata(file);
    } else {
        throw new Error(`Unsupported file type: ${fileType}`);
    }
}

/**
 * Extract metadata from image files locally using exifr
 */
async function extractImageMetadata(file) {
    const metadata = {
        fileName: file.name,
        fileSize: formatFileSize(file.size),
        fileType: file.type,
        lastModified: new Date(file.lastModified).toISOString(),
        isAIGenerated: false,
        aiGenerator: null,
        aiPrompt: null,
        confidence: 0,
    };

    try {
        const arrayBuffer = await file.arrayBuffer();
        const exif = await exifr.parse(arrayBuffer, {
            all: true,
            xmp: true,
            icc: false,
            iptc: true,
            tiff: true,
            jfif: true,
            ihdr: true,
            // PNG specific
            pHYs: true,
            iCCP: true,
        });

        if (exif) {
            // Copy all EXIF data
            Object.assign(metadata, exif);

            // Check all text fields for AI signatures
            const fieldsToCheck = [
                'Description', 'ImageDescription', 'UserComment',
                'parameters', 'Comment', 'prompt', 'Software',
                'Artist', 'Copyright', 'Make', 'Model', 'XPComment',
                'XPSubject', 'XPTitle', 'XPKeywords', 'Creator',
                'Rights', 'Title', 'Subject', 'Keywords'
            ];

            for (const field of fieldsToCheck) {
                if (exif[field]) {
                    const value = String(exif[field]);
                    const detection = detectAIFromString(value);
                    if (detection.generator && detection.confidence > metadata.confidence) {
                        metadata.isAIGenerated = true;
                        metadata.aiGenerator = detection.generator;
                        metadata.aiPrompt = detection.prompt;
                        metadata.confidence = detection.confidence;
                    }
                }
            }

            // Also check for Stable Diffusion 'parameters' field specifically
            if (exif.parameters && !metadata.aiGenerator) {
                metadata.isAIGenerated = true;
                metadata.aiGenerator = 'Stable Diffusion';
                metadata.aiPrompt = exif.parameters;
                metadata.confidence = 0.95;
            }
        }

        // Deep scan binary data for hidden prompts
        const deepScanResult = await deepScanForPrompts(arrayBuffer);
        if (deepScanResult && deepScanResult.confidence > metadata.confidence) {
            metadata.isAIGenerated = true;
            metadata.aiGenerator = deepScanResult.generator || 'AI Generator';
            metadata.aiPrompt = deepScanResult.prompt;
            metadata.confidence = deepScanResult.confidence;
        }

    } catch (error) {
        console.error('Error extracting image metadata:', error);
    }

    // C2PA Scan
    try {
        const c2paResult = await readC2pa(file);
        if (c2paResult) {
            metadata.C2PA = c2paResult.manifest;
            if (c2paResult.aiDetection.isAIGenerated) {
                metadata.isAIGenerated = true;
                metadata.aiGenerator = c2paResult.aiDetection.aiGenerator || metadata.aiGenerator || 'C2PA Authenticated AI';
                metadata.confidence = 1.0;
                if (!metadata.aiPrompt) {
                    metadata.aiPrompt = '[C2PA Action/Assertion details embedded]';
                }
            }
        }
    } catch (error) {
        console.warn('Error reading C2PA from image:', error);
    }

    return metadata;
}

/**
 * Extract metadata from video files
 * Uses comprehensive client-side extraction with mp4box.js
 */
async function extractVideoMetadata(file) {
    const metadata = {
        fileName: file.name,
        fileSize: formatFileSize(file.size),
        fileType: file.type,
        lastModified: new Date(file.lastModified).toISOString(),
        isAIGenerated: false,
        aiGenerator: null,
        aiPrompt: null,
        confidence: 0,
    };

    // Client-side MP4 parsing
    try {
        const mp4Metadata = await extractMP4Metadata(file);
        Object.assign(metadata, mp4Metadata);
    } catch (mp4Error) {
        console.error('MP4Box extraction failed:', mp4Error);
    }

    // Deep scan binary data for AI prompts
    try {
        const arrayBuffer = await file.arrayBuffer();
        const deepScanResult = await deepScanForPrompts(arrayBuffer);
        if (deepScanResult && deepScanResult.confidence > metadata.confidence) {
            metadata.isAIGenerated = true;
            metadata.aiGenerator = deepScanResult.generator || 'AI Generator';
            metadata.aiPrompt = deepScanResult.prompt;
            metadata.confidence = deepScanResult.confidence;
        }
    } catch (scanError) {
        console.error('Deep scan failed:', scanError);
    }

    // C2PA Scan
    try {
        const c2paResult = await readC2pa(file);
        if (c2paResult) {
            metadata.C2PA = c2paResult.manifest;
            if (c2paResult.aiDetection.isAIGenerated) {
                metadata.isAIGenerated = true;
                metadata.aiGenerator = c2paResult.aiDetection.aiGenerator || metadata.aiGenerator || 'C2PA Authenticated AI';
                metadata.confidence = 1.0;
                if (!metadata.aiPrompt) {
                    metadata.aiPrompt = '[C2PA Action/Assertion details embedded]';
                }
            }
        }
    } catch (error) {
        console.warn('Error reading C2PA from video:', error);
    }

    metadata.extractionMethod = 'client-side';
    return metadata;
}

/**
 * Comprehensive MP4 metadata extraction using mp4box.js (client-side)
 * Extracts full track info, box structure, and user data atoms
 */
async function extractMP4Metadata(file) {
    return new Promise((resolve, reject) => {
        const mp4boxfile = createMP4File();
        const result = {};

        mp4boxfile.onReady = function (info) {
            try {
                // ---- General file info ----
                result.General = {
                    format: 'MP4 (ISOBMFF)',
                    duration: info.duration ? (info.duration / info.timescale).toFixed(3) + 's' : 'unknown',
                    durationSeconds: info.duration ? (info.duration / info.timescale) : null,
                    timescale: info.timescale,
                    isFragmented: info.isFragmented,
                    isProgressive: info.isProgressive,
                    hasIOD: info.hasIOD,
                    brands: info.brands ? info.brands.join(', ') : undefined,
                    created: info.created ? new Date(info.created).toISOString() : undefined,
                    modified: info.modified ? new Date(info.modified).toISOString() : undefined,
                    totalTracks: (info.tracks || []).length,
                };

                // ---- Video tracks ----
                if (info.videoTracks && info.videoTracks.length > 0) {
                    const vt = info.videoTracks[0];
                    result.Video = {
                        codec: vt.codec,
                        width: vt.video ? vt.video.width : vt.track_width,
                        height: vt.video ? vt.video.height : vt.track_height,
                        trackWidth: vt.track_width,
                        trackHeight: vt.track_height,
                        duration: vt.duration ? (vt.duration / vt.timescale).toFixed(3) + 's' : undefined,
                        timescale: vt.timescale,
                        numberOfSamples: vt.nb_samples,
                        bitDepth: vt.video ? vt.video.bit_depth : undefined,
                        frameRate: vt.nb_samples && vt.duration && vt.timescale
                            ? (vt.nb_samples / (vt.duration / vt.timescale)).toFixed(3) + ' fps'
                            : undefined,
                        language: vt.language !== 'und' ? vt.language : undefined,
                        createdDate: vt.created ? new Date(vt.created).toISOString() : undefined,
                        modifiedDate: vt.modified ? new Date(vt.modified).toISOString() : undefined,
                        handler: vt.handler || undefined,
                        name: vt.name || undefined,
                    };
                    // Set top-level convenience fields
                    result.width = result.Video.width;
                    result.height = result.Video.height;
                    result.codec = result.Video.codec;
                    result.frameRate = result.Video.frameRate;
                    result.duration = result.General.duration;
                }

                // Additional video tracks
                if (info.videoTracks && info.videoTracks.length > 1) {
                    result.AdditionalVideoTracks = info.videoTracks.slice(1).map(vt => ({
                        codec: vt.codec,
                        width: vt.video ? vt.video.width : vt.track_width,
                        height: vt.video ? vt.video.height : vt.track_height,
                        numberOfSamples: vt.nb_samples,
                    }));
                }

                // ---- Audio tracks ----
                if (info.audioTracks && info.audioTracks.length > 0) {
                    const at = info.audioTracks[0];
                    result.Audio = {
                        codec: at.codec,
                        sampleRate: at.audio ? at.audio.sample_rate : undefined,
                        channelCount: at.audio ? at.audio.channel_count : undefined,
                        sampleSize: at.audio ? at.audio.sample_size : undefined,
                        duration: at.duration ? (at.duration / at.timescale).toFixed(3) + 's' : undefined,
                        numberOfSamples: at.nb_samples,
                        language: at.language !== 'und' ? at.language : undefined,
                        handler: at.handler || undefined,
                        name: at.name || undefined,
                    };
                }

                // Additional audio tracks
                if (info.audioTracks && info.audioTracks.length > 1) {
                    result.AdditionalAudioTracks = info.audioTracks.slice(1).map(at => ({
                        codec: at.codec,
                        sampleRate: at.audio ? at.audio.sample_rate : undefined,
                        channelCount: at.audio ? at.audio.channel_count : undefined,
                        language: at.language !== 'und' ? at.language : undefined,
                    }));
                }

                // ---- Subtitle / text tracks ----
                if (info.subtitleTracks && info.subtitleTracks.length > 0) {
                    result.SubtitleTracks = info.subtitleTracks.map(st => ({
                        codec: st.codec,
                        language: st.language !== 'und' ? st.language : undefined,
                        handler: st.handler || undefined,
                        name: st.name || undefined,
                    }));
                }

                // ---- Other/metadata tracks ----
                if (info.metadataTracks && info.metadataTracks.length > 0) {
                    result.MetadataTracks = info.metadataTracks.map(mt => ({
                        codec: mt.codec,
                        handler: mt.handler || undefined,
                        name: mt.name || undefined,
                    }));
                }

                // ---- Hint tracks ----
                if (info.hintTracks && info.hintTracks.length > 0) {
                    result.HintTracks = info.hintTracks.map(ht => ({
                        codec: ht.codec,
                        handler: ht.handler || undefined,
                    }));
                }

                // ---- All tracks summary (for the tree view) ----
                if (info.tracks && info.tracks.length > 0) {
                    result.AllTracks = info.tracks.map((t, i) => ({
                        id: t.id,
                        type: t.type,
                        codec: t.codec,
                        duration: t.duration ? (t.duration / t.timescale).toFixed(3) + 's' : undefined,
                        numberOfSamples: t.nb_samples,
                        language: t.language !== 'und' ? t.language : undefined,
                        size: t.size ? formatFileSize(t.size) : undefined,
                        bitrate: t.bitrate ? Math.round(t.bitrate / 1000) + ' kbps' : undefined,
                    }));
                }

                // ---- Box structure (top-level atoms) ----
                try {
                    if (mp4boxfile.boxes && mp4boxfile.boxes.length > 0) {
                        result.BoxStructure = extractBoxStructure(mp4boxfile.boxes, 0);
                    }
                } catch (e) {
                    console.warn('Could not extract box structure:', e);
                }

                // ---- User data (udta) extraction ----
                try {
                    const udta = findBox(mp4boxfile.boxes, 'udta');
                    if (udta) {
                        result.UserData = extractUserData(udta);
                    }
                } catch (e) {
                    console.warn('Could not extract user data:', e);
                }

                // ---- QuickTime mdta keys/ilst metadata (moov > meta) ----
                try {
                    const qtMeta = extractQuickTimeMetadata(mp4boxfile.boxes);
                    if (qtMeta && Object.keys(qtMeta).length > 0) {
                        result.QuickTimeMetadata = qtMeta;
                    }
                } catch (e) {
                    console.warn('Could not extract QuickTime metadata:', e);
                }

                // ---- Check for AI signatures in all string metadata ----
                const allStrings = JSON.stringify(result);
                const aiDetection = detectAIFromString(allStrings);
                if (aiDetection.generator && aiDetection.confidence > 0.5) {
                    result.isAIGenerated = true;
                    result.aiGenerator = aiDetection.generator;
                    result.aiPrompt = aiDetection.prompt;
                    result.confidence = aiDetection.confidence;
                }

                // Store the full parsed info for the metadata tree
                result.videoMetadata = {
                    General: result.General,
                    Video: result.Video,
                    Audio: result.Audio,
                    AllTracks: result.AllTracks,
                    BoxStructure: result.BoxStructure,
                    UserData: result.UserData,
                    QuickTimeMetadata: result.QuickTimeMetadata,
                };

            } catch (e) {
                console.error('Error processing MP4 info:', e);
            }

            resolve(result);
        };

        mp4boxfile.onError = function (error) {
            console.error('MP4Box parse error:', error);
            reject(error);
        };

        file.arrayBuffer().then(arrayBuffer => {
            arrayBuffer.fileStart = 0;
            mp4boxfile.appendBuffer(arrayBuffer);
            mp4boxfile.flush();
        }).catch(reject);
    });
}

/**
 * Recursively extract MP4 box structure for display
 */
function extractBoxStructure(boxes, depth) {
    if (!boxes || depth > 5) return undefined;

    return boxes.map(box => {
        const entry = {
            type: box.type,
            size: box.size ? formatFileSize(box.size) : undefined,
        };

        // Extract interesting fields from specific box types
        if (box.type === 'ftyp') {
            entry.majorBrand = box.major_brand;
            entry.minorVersion = box.minor_version;
            entry.compatibleBrands = box.compatible_brands ? box.compatible_brands.join(', ') : undefined;
        } else if (box.type === 'mvhd') {
            entry.timescale = box.timescale;
            entry.duration = box.duration;
            entry.creationTime = box.creation_time ? new Date(box.creation_time).toISOString() : undefined;
            entry.modificationTime = box.modification_time ? new Date(box.modification_time).toISOString() : undefined;
            entry.rate = box.rate;
            entry.volume = box.volume;
        } else if (box.type === 'tkhd') {
            entry.trackId = box.track_id;
            entry.width = box.width;
            entry.height = box.height;
            entry.volume = box.volume;
        } else if (box.type === 'hdlr') {
            entry.handlerType = box.handler;
            entry.name = box.name;
        } else if (box.type === 'mdhd') {
            entry.timescale = box.timescale;
            entry.duration = box.duration;
            entry.language = box.language;
        } else if (box.type === 'stsd') {
            if (box.entries && box.entries.length > 0) {
                entry.entries = box.entries.map(e => ({
                    type: e.type,
                    width: e.width,
                    height: e.height,
                    horizResolution: e.horizresolution,
                    vertResolution: e.vertresolution,
                    sampleRate: e.samplerate,
                    channelCount: e.channel_count,
                    sampleSize: e.samplesize,
                })).filter(e => Object.values(e).some(v => v !== undefined));
            }
        }

        // Recurse into child boxes
        if (box.boxes && box.boxes.length > 0) {
            entry.children = extractBoxStructure(box.boxes, depth + 1);
        }

        return entry;
    });
}

/**
 * Find a box by type recursively in the box tree
 */
function findBox(boxes, type) {
    if (!boxes) return null;
    for (const box of boxes) {
        if (box.type === type) return box;
        if (box.boxes) {
            const found = findBox(box.boxes, type);
            if (found) return found;
        }
    }
    return null;
}

/**
 * iTunes-style metadata atom name mapping
 */
const ITUNES_ATOM_NAMES = {
    '©too': 'Encoder',
    '©nam': 'Title',
    '©ART': 'Artist',
    '©alb': 'Album',
    '©day': 'Date',
    '©cmt': 'Comment',
    '©gen': 'Genre',
    '©wrt': 'Writer',
    '©des': 'Description',
    '©enc': 'EncodedBy',
    '©grp': 'Grouping',
    '©lyr': 'Lyrics',
    '©prd': 'Producer',
    '©dir': 'Director',
    '©src': 'Source',
    '©prf': 'Performers',
    '©inf': 'Information',
    '©req': 'Requirements',
    '©fmt': 'Format',
    '©swr': 'SoftwareVersion',
    'aART': 'AlbumArtist',
    'cprt': 'Copyright',
    'desc': 'Description',
    'ldes': 'LongDescription',
    'tvsh': 'TVShow',
    'tvnn': 'TVNetwork',
    'tven': 'TVEpisodeID',
    'tvsn': 'TVSeason',
    'tves': 'TVEpisode',
};

/**
 * Decode a data box value based on its type flag
 */
function decodeDataBoxValue(dataBox) {
    // mp4box.js data boxes have a 'data' field (Uint8Array) and sometimes a 'type' field
    const raw = dataBox.data;
    if (!raw || raw.length === 0) return null;

    // Try to detect the data type from the box's type_set flags
    // Type 1 = UTF-8 text, Type 2 = UTF-16 text, Type 0/21 = integer, Type 13/14 = image
    const typeFlag = dataBox.type_set !== undefined ? dataBox.type_set : 1; // Default to text

    // For integer types
    if (typeFlag === 0 || typeFlag === 21) {
        if (raw.length === 1) return raw[0];
        if (raw.length === 2) return (raw[0] << 8) | raw[1];
        if (raw.length === 4) return (raw[0] << 24) | (raw[1] << 16) | (raw[2] << 8) | raw[3];
    }

    // Try UTF-8 text decode
    try {
        const textDecoder = new TextDecoder('utf-8', { fatal: false });
        const value = textDecoder.decode(raw);
        // Filter out strings that are mostly non-printable
        const printable = value.replace(/[^\x20-\x7E\n\r\t]/g, '');
        if (printable.length > 0 && printable.length >= value.length * 0.5) {
            return printable.trim();
        }
    } catch (e) {
        // Fall through
    }

    return null;
}

/**
 * Extract user data from udta box — handles iTunes-style ilst atoms
 */
function extractUserData(udtaBox) {
    const userData = {};

    if (!udtaBox.boxes) return undefined;

    for (const box of udtaBox.boxes) {
        if (box.type === 'meta' && box.boxes) {
            for (const metaChild of box.boxes) {
                if (metaChild.type === 'ilst') {
                    // iTunes-style ilst - may have .boxes or .list
                    if (metaChild.boxes) {
                        for (const item of metaChild.boxes) {
                            try {
                                const atomName = ITUNES_ATOM_NAMES[item.type] || item.type;
                                if (item.boxes) {
                                    for (const dataBox of item.boxes) {
                                        const value = decodeDataBoxValue(dataBox);
                                        if (value !== null) {
                                            userData[atomName] = value;
                                        }
                                    }
                                }
                            } catch (e) {
                                // Skip unparseable items
                            }
                        }
                    }
                    // QT-style ilst has .list (index → box)
                    if (metaChild.list) {
                        for (const [index, dataBox] of Object.entries(metaChild.list)) {
                            try {
                                const value = decodeDataBoxValue(dataBox);
                                if (value !== null) {
                                    userData[`item_${index}`] = value;
                                }
                            } catch (e) {
                                // Skip
                            }
                        }
                    }
                } else if (metaChild.type === 'hdlr') {
                    userData['metaHandler'] = metaChild.handler || metaChild.name || metaChild.type;
                }
            }
        }
        // Direct text in udta children (for non-meta boxes)
        if (box.data && box.type !== 'meta') {
            try {
                const textDecoder = new TextDecoder('utf-8', { fatal: false });
                const raw = textDecoder.decode(box.data);
                const printable = raw.replace(/[^\x20-\x7E\n\r\t]/g, '').trim();
                if (printable.length > 2) {
                    const atomName = ITUNES_ATOM_NAMES[box.type] || box.type;
                    userData[atomName] = printable;
                }
            } catch (e) {
                // Skip
            }
        }
    }

    return Object.keys(userData).length > 0 ? userData : undefined;
}

/**
 * Extract QuickTime mdta-style metadata from moov > meta box
 * This uses a keys box (key names) paired with an ilst box (values)
 */
function extractQuickTimeMetadata(boxes) {
    const result = {};
    if (!boxes) return result;

    // Find all meta boxes (could be at moov level or nested)
    const metaBoxes = findAllBoxes(boxes, 'meta');

    for (const metaBox of metaBoxes) {
        if (!metaBox.boxes) continue;

        // Check if this meta box has mdta handler
        const hdlr = metaBox.boxes.find(b => b.type === 'hdlr');
        const isMdta = hdlr && (hdlr.handler === 'mdta' || hdlr.name === 'mdta');

        if (isMdta) {
            // Find keys and ilst boxes
            const keysBox = metaBox.boxes.find(b => b.type === 'keys');
            const ilstBox = metaBox.boxes.find(b => b.type === 'ilst');

            if (keysBox && keysBox.keys && ilstBox && ilstBox.list) {
                // Pair up keys with ilst values
                for (const [indexStr, keyName] of Object.entries(keysBox.keys)) {
                    const index = parseInt(indexStr, 10);
                    const dataBox = ilstBox.list[index];

                    if (dataBox && keyName) {
                        // Make key name more readable
                        const friendlyKey = keyName
                            .replace('com.apple.quicktime.', '')
                            .replace('com.apple.', '')
                            .replace(/\./g, '_');

                        const value = decodeDataBoxValue(dataBox);
                        if (value !== null) {
                            result[friendlyKey] = value;
                        }
                    }
                }
            }
        }
    }

    return result;
}

/**
 * Find all boxes of a given type (non-recursive at each level, but recursive through children)
 */
function findAllBoxes(boxes, type) {
    const found = [];
    if (!boxes) return found;
    for (const box of boxes) {
        if (box.type === type) found.push(box);
        if (box.boxes) found.push(...findAllBoxes(box.boxes, type));
    }
    return found;
}

/**
 * Deep scan binary data for AI prompts (client-side)
 * Only scans first and last 1MB to avoid browser freeze on large files
 */
async function deepScanForPrompts(arrayBuffer) {
    const MAX_SCAN_SIZE = 1024 * 1024; // 1MB limit per section
    const uint8Array = new Uint8Array(arrayBuffer);

    // Only scan first and last 1MB to avoid freezing on large video files
    let textToScan = '';
    const textDecoder = new TextDecoder('utf-8', { fatal: false });

    if (uint8Array.length <= MAX_SCAN_SIZE * 2) {
        // Small file - scan all
        textToScan = textDecoder.decode(uint8Array);
    } else {
        // Large file - scan only first and last 1MB
        const firstPart = uint8Array.slice(0, MAX_SCAN_SIZE);
        const lastPart = uint8Array.slice(-MAX_SCAN_SIZE);
        textToScan = textDecoder.decode(firstPart) + '\n---\n' + textDecoder.decode(lastPart);
    }

    // First try string detection
    const detection = detectAIFromString(textToScan);
    if (detection.generator && detection.confidence > 0.5) {
        return detection;
    }

    // Pattern-based scanning - use non-greedy patterns to avoid catastrophic backtracking
    const patterns = [
        // Midjourney
        { regex: /--ar\s+\d+:\d+.*?(?=\n|$)/gim, generator: 'Midjourney' },
        { regex: /\/imagine\s+.{10,200}?(?=\n|$)/gim, generator: 'Midjourney' },
        { regex: /Job ID:\s*[a-f0-9-]+/gi, generator: 'Midjourney' },

        // Stable Diffusion
        { regex: /Steps:\s*\d+,\s*Sampler:\s*\w+/gi, generator: 'Stable Diffusion' },
        { regex: /Negative prompt:.{10,500}?Steps:/gis, generator: 'Stable Diffusion' },

        // Generic JSON prompts
        { regex: /"prompt"\s*:\s*"([^"]{20,500})"/gi, generator: 'AI Generator' },
        { regex: /"text_prompt"\s*:\s*"([^"]{20,500})"/gi, generator: 'AI Generator' },
        { regex: /"positive_prompt"\s*:\s*"([^"]{20,500})"/gi, generator: 'AI Generator' },

        // ComfyUI workflow
        { regex: /"class_type"\s*:\s*"[^"]*Prompt/gi, generator: 'ComfyUI' },
    ];

    for (const { regex, generator } of patterns) {
        try {
            const matches = textToScan.match(regex);
            if (matches && matches.length > 0) {
                const prompt = matches[0].length > 500 ? matches[0].substring(0, 500) + '...' : matches[0];
                return { prompt, generator, confidence: 0.7 };
            }
        } catch (e) {
            // Skip this pattern if it fails
            console.warn('Pattern failed:', e);
        }
    }

    return null;
}

/**
 * Format file size in human-readable format
 */
function formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

export default extractMetadata;
